// What a place in the store can actually MEASURE — drives the "Log your own change"
// form: which places are offered, and which metrics each one offers.
//
// Built from each zone's own configuration, never from its type alone: a tripwire
// can't see who stops or buys (no engagement / conversion / per-visitor time), an
// estimated time in zone needs in + out on one tripwire, and a transit time needs
// transition mode with a source. Entrance-like zones (1 Entrance, 3 Combined
// Entrance/Exit) count STORE traffic; every other zone counts ZONE traffic.
//
// Pure and framework-free (shared by the live Actions tab and the demo, and
// unit-tested). The order of a list is the order offered: the first is the default
// goal — the metric the action is judged on.

export const WHOLE_STORE = 'Whole store'

const ENTRANCE_LIKE = [1, 3]
const EXIT = 2
// Staff positions (cash registers, behind the desks) — a capacity question, not a
// place where a change to the shop floor is measured.
const STAFF = [999, 15, 16, 17]
const PRODUCT_LIKE = [6, 7]          // product + demo: full funnel with a polygon
const DISPLAY = 8
const WAITING = [4, 12, 13, 14]      // the measured time IS the wait
const INTERACTION = [5, 9, 10, 11]   // the measured time IS the service

const GROUPS = [
  { label: 'Store', types: ENTRANCE_LIKE },
  { label: 'Checkouts', types: [4, 5] },
  { label: 'Service points', types: [9, 10, 11, 12, 13, 14] },
  { label: 'Product, demo & display zones', types: [6, 7, 8] },
]

const parse = (v) => {
  if (v == null) return null
  if (typeof v !== 'string') return v
  try { return JSON.parse(v) } catch { return null }
}
const settingsOf = (z) => parse(z && (z.DetectionZoneSettings ?? z.Settings)) || {}

export function hasPolygon(z) {
  const pts = parse(z && (z.DetectionZonePointArray ?? z.Polygon))
  return Array.isArray(pts) && pts.length >= 3
}
export function hasTripwire(z) {
  const tw = parse(z && z.DetectionZoneTripwirePoints)
  return Array.isArray(tw) && tw.length > 0
}
export const isEntranceLike = (z) => !!z && ENTRANCE_LIKE.includes(z.DetectionZoneTypeId)

// 'dwell_est' | 'transit' | null — the tripwire estimate this zone is set up for.
export function estimateOf(z) {
  const st = settingsOf(z)
  if (!st.UseTripwireForDwell) return null
  if ((st.DwellCalculationMode || 'dwell') === 'transition') return st.TransitionSourceKey ? 'transit' : null
  return 'dwell_est'
}

// Can a change be logged against this zone at all?
export function isLoggable(z) {
  if (!z || z.IsActive === false) return false
  const t = z.DetectionZoneTypeId
  if (t === EXIT || STAFF.includes(t)) return false
  return hasPolygon(z) || hasTripwire(z)
}

// The metrics this place can measure, default goal first. `zone` null = whole store.
export function watchableFor(zone) {
  if (!zone) return ['traffic', 'purchase_rate']
  const t = zone.DetectionZoneTypeId
  // An entrance change is judged on the whole store's entries, with this door's own
  // count beside it — so shoppers merely shifting between doors isn't read as growth.
  if (ENTRANCE_LIKE.includes(t)) return ['store_traffic', 'traffic']
  if (hasPolygon(zone)) {
    if (PRODUCT_LIKE.includes(t)) {
      const m = ['engagement', 'conversion', 'dwell', 'reach', 'traffic']
      if (settingsOf(zone).TriggerAction) m.push('service_response')
      return m
    }
    if (t === DISPLAY) return ['engagement', 'dwell', 'reach', 'traffic']
    if (WAITING.includes(t)) return ['wait', 'reach', 'traffic']
    if (INTERACTION.includes(t)) return ['interaction', 'reach', 'traffic']
    return ['dwell', 'reach', 'traffic']
  }
  // Tripwire only: who ARRIVES — never who stops or buys, or for how long each.
  const m = ['reach', 'traffic']
  const est = estimateOf(zone)
  if (est) m.push(est)
  if (PRODUCT_LIKE.includes(t) || t === DISPLAY) m.push('purchase_rate')
  return m
}

// The places to offer, grouped and ordered: whole store first, then each group.
export function placeGroups(zones) {
  const live = (zones || []).filter(isLoggable)
  const seen = new Set()
  const groups = []
  for (const g of GROUPS) {
    const names = live
      .filter((z) => g.types.includes(z.DetectionZoneTypeId))
      .map((z) => z.DetectionZoneName)
      .filter((n) => n && !seen.has(n) && seen.add(n))
      .sort((a, b) => a.localeCompare(b))
    if (names.length) groups.push({ label: g.label, names })
  }
  return groups
}

export function zoneByName(zones, name) {
  if (!name || name === WHOLE_STORE) return null
  return (zones || []).find((z) => z.DetectionZoneName === name && z.IsActive !== false) || null
}
